"use client"

import type { IconButtonProps } from "@chakra-ui/react"
import { ClientOnly, IconButton, Skeleton, useEnvironmentContext } from "@chakra-ui/react"
import { ThemeProvider, useTheme } from "next-themes"
import type { ThemeProviderProps } from "next-themes"
import { forwardRef, useEffect, useState } from "react"
import { LuMoon, LuSun } from "react-icons/lu"

/**
 * localStorage key holding the user's Colour Mode Preference ("light" | "dark" | "system").
 * The pre-paint script in `hub/index.html` reads the same key, so keep the two in step.
 */
export const COLOUR_MODE_STORAGE_KEY = "logiq-colour-mode"

export type ColourModePreference = "light" | "dark" | "system"

export interface ColorModeProviderProps extends ThemeProviderProps {}

export function ColorModeProvider(props: ColorModeProviderProps) {
  return (
    <ThemeProvider
      attribute="class"
      storageKey={COLOUR_MODE_STORAGE_KEY}
      enableSystem
      disableTransitionOnChange
      {...props}
    />
  )
}

export function useColorMode() {
  const { theme, resolvedTheme, setTheme } = useTheme()
  const toggleColorMode = () => {
    setTheme(resolvedTheme === "light" ? "dark" : "light")
  }
  return {
    /** Resolved mode actually painted: "light" or "dark" (undefined outside a provider). */
    colorMode: resolvedTheme,
    /** What the user picked, including "system". */
    preference: theme as ColourModePreference | undefined,
    setColorMode: setTheme,
    toggleColorMode,
  }
}

/**
 * Resolved Colour Mode for code inside a section, for libraries that need it as a
 * value rather than CSS (ReactFlow `colorMode`, map styles). Sections render in a
 * shadow root and have no colour-mode provider, so this reads the `light` / `dark`
 * class the hub sets on the section's `#section-body`, and follows changes to it.
 * Outside a section it reads `<html>`.
 */
export function useSectionColorMode(): "light" | "dark" {
  const { getRootNode } = useEnvironmentContext()

  const target = (): HTMLElement => {
    const root = getRootNode() as ShadowRoot | Document
    return (root.querySelector?.("#section-body") as HTMLElement | null) ?? document.documentElement
  }
  const read = () => (target().classList.contains("dark") ? "dark" : "light")

  const [mode, setMode] = useState<"light" | "dark">(read)

  useEffect(() => {
    const element = target()
    const observer = new MutationObserver(() => setMode(read()))
    observer.observe(element, { attributes: true, attributeFilter: ["class"] })
    setMode(read())
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRootNode])

  return mode
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { colorMode } = useColorMode()
  return colorMode === "light" ? light : dark
}

export function ColorModeIcon() {
  const { colorMode } = useColorMode()
  return colorMode === "light" ? <LuSun /> : <LuMoon />
}

interface ColorModeButtonProps extends Omit<IconButtonProps, "aria-label"> {}

export const ColorModeButton = forwardRef<HTMLButtonElement, ColorModeButtonProps>(function ColorModeButton(
  props,
  ref
) {
  const { toggleColorMode } = useColorMode()
  return (
    <ClientOnly fallback={<Skeleton boxSize="8" />}>
      <IconButton
        onClick={toggleColorMode}
        variant="ghost"
        aria-label="Toggle color mode"
        size="sm"
        ref={ref}
        {...props}
        css={{
          _icon: {
            width: "5",
            height: "5",
          },
        }}
      >
        <ColorModeIcon />
      </IconButton>
    </ClientOnly>
  )
})
